import { Navigate, Outlet, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import authService from '@/core/auth/services/auth-service';
import { useTimer } from '@/shared/hooks/export';
import { Loading } from '@/shared/components/export';

type Props = {
  children?: JSX.Element | JSX.Element[];
};

const perHostLoginPage =
  process.env[
    `REACT_APP_LOGIN_PATH_${window.location.hostname.toUpperCase().replaceAll('.', '_')}`
  ];

const loginPage = perHostLoginPage
  ? perHostLoginPage
  : (process.env.REACT_APP_LOGIN_PATH as string);

export default function AuthProvider({ children }: Props) {
  const [token, setToken] = useState<string>();
  const [loading, setLoading] = useState(true);

  const [query] = useSearchParams();

  const { logout: auth0Logout } = useAuth0();

  const logoutAndClear = () => authService.logout().then(() => setToken(undefined));

  // Logging out of the website deletes the shared permissions cookie, so its
  // absence next to an Auth0 login means the session ended over there.
  const loggedOutElsewhere = () =>
    authService.getCredentials()?.token.source === 'auth0' &&
    authService.getPermissionsCookie() === undefined;

  const followLogoutElsewhere = () =>
    authService
      .logout()
      .then(() => auth0Logout({ logoutParams: { returnTo: window.location.origin } }));

  const authCheck = () => {
    if (loggedOutElsewhere()) return followLogoutElsewhere();

    if (!authService.tokenValid()) {
      // Try to silently refresh before logging the user out.
      authService
        .getAccessToken()
        .then(refreshed => {
          if (refreshed?.access_token) {
            setToken(refreshed.access_token);
          } else {
            logoutAndClear();
          }
        })
        .catch(() => logoutAndClear());
    }
  };

  useEffect(() => {
    if (loggedOutElsewhere()) {
      followLogoutElsewhere();
      return;
    }

    authService
      .getAccessToken()
      .then((value: any) => {
        const from = query.get('from');
        const chatKeys = query.get('chat-keys');

        if (value?.access_token) {
          setToken(value?.access_token);
        } else if (from === 'external') {
          const currentPath = window.location.pathname;
          const hash = window.location.hash;
          if (chatKeys) {
            localStorage.setItem('redirectPath', `${currentPath}?chat-keys=true${hash}`);
          } else {
            localStorage.setItem('redirectPath', currentPath + hash);
          }
        } else {
          localStorage.setItem('redirectPath', window.location.pathname + window.location.hash);
        }

        query.delete('from');
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
    // eslint-disable-next-line
  }, []);

  useEffect(
    () =>
      authService.onApiError(message => {
        console.warn('API ERROR', message);

        if (!authService.tokenValid()) {
          console.warn('Logout', message);

          authService.logout().then(() => {
            setToken(undefined);
          });
        }
      }),
    [],
  );

  useTimer(60_000, () => {
    authCheck();
  });

  if (loading) return <Loading />;

  if (token) return <Outlet />;
  else {
    return <Navigate to={loginPage} />;
  }
}
